import { motion } from "framer-motion";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { useHive } from "@/context/HiveContext";

// OCTON HIVE ORB — the single living orb of the OCTON fleet.
// Breathes when idle, glows neural-blue on live voice, pulses crimson on high
// prediction-error OR when the fleet raises a critical HIVE alert. A small node
// dot shows live connection to the OCTON HIVE bus.
export function AvaHiveOrb({ status = "idle", speaking = false, predictionError = 0.3, onClick, size = 240 }) {
  const { online: hiveOnline, hiveAlert } = useHive();
  const live = status === "live" || status === "connecting";
  const hot = predictionError > 0.55 || hiveAlert;
  const core = hot ? "#FF3B30" : live ? "#0047FF" : "#CCFF00";
  const ring = speaking ? "#CCFF00" : core;
  const rippleFast = speaking || hiveAlert;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}
         data-orb data-testid="ava-hive-orb" data-hive-alert={hiveAlert ? "1" : "0"}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{ border: `1px solid ${ring}`, opacity: 0.25 }}
          initial={{ width: size * 0.55, height: size * 0.55, opacity: 0 }}
          animate={{ width: [size * 0.55, size], height: [size * 0.55, size], opacity: [0.4, 0] }}
          transition={{ duration: rippleFast ? 1.4 : 3, repeat: Infinity, delay: i * (rippleFast ? 0.45 : 1), ease: "easeOut" }}
        />
      ))}
      <motion.button
        type="button"
        onClick={onClick}
        data-testid="ava-hive-orb-btn"
        aria-label="Toggle OCTON live voice"
        className="relative rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#CCFF00]"
        style={{
          width: size * 0.62,
          height: size * 0.62,
          background: `radial-gradient(circle at 35% 30%, ${core}, #05060a 78%)`,
          boxShadow: `0 0 60px -8px ${core}, inset 0 0 40px -12px ${ring}`,
        }}
        animate={{ scale: rippleFast ? [1, 1.07, 1] : [1, 1.04, 1] }}
        transition={{ duration: rippleFast ? 0.9 : 3.4, repeat: Infinity, ease: "easeInOut" }}
        whileTap={{ scale: 0.94 }}
      >
        <div
          className="absolute inset-[10%] rounded-full opacity-40"
          style={{ background: `conic-gradient(from 0deg, transparent, ${ring}55, transparent)` }}
        />
        {status === "connecting" ? (
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        ) : live ? (
          <Mic className="w-9 h-9 text-white" />
        ) : (
          <MicOff className="w-9 h-9" style={{ color: "#05060a" }} />
        )}
      </motion.button>

      {/* HIVE fleet-link node dot */}
      <motion.span
        className="absolute rounded-full"
        title={hiveOnline ? "Linked to OCTON HIVE" : "OCTON HIVE offline"}
        data-testid="ava-hive-node-dot"
        style={{
          width: size * 0.07, height: size * 0.07,
          right: size * 0.16, top: size * 0.16,
          background: hiveAlert ? "#FF3B30" : hiveOnline ? "#CCFF00" : "#3A3F4B",
          boxShadow: hiveOnline || hiveAlert ? `0 0 12px ${hiveAlert ? "#FF3B30" : "#CCFF00"}` : "none",
        }}
        animate={hiveOnline || hiveAlert ? { opacity: [1, 0.35, 1] } : { opacity: 0.5 }}
        transition={{ duration: hiveAlert ? 0.7 : 2.2, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}
